import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Search, Filter, Calendar as CalIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  awaiting_payment: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  confirmed: "bg-green-100 text-green-800 border-green-300",
  cancelled: "bg-gray-200 text-gray-700 border-gray-300",
  expired: "bg-zinc-200 text-zinc-700 border-zinc-300",
};

export const StatusBadge = ({ status }) => (
  <span
    data-testid={`status-${status}`}
    className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS_COLORS[status] || "bg-gray-100 text-gray-700 border-gray-300"}`}
  >
    {status}
  </span>
);

export const PaymentBadge = ({ payment_status }) => {
  const map = {
    unpaid: "bg-orange-100 text-orange-800 border-orange-300",
    paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
    refunded: "bg-zinc-200 text-zinc-700 border-zinc-300",
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${map[payment_status] || "bg-gray-100 text-gray-700 border-gray-300"}`}>
      {payment_status}
    </span>
  );
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/bookings`, {
        params: { q: q || undefined, status: status || undefined, payment_status: paymentStatus || undefined },
        withCredentials: true,
      });
      setBookings(r.data || []);
    } finally {
      setLoading(false);
    }
  }, [q, status, paymentStatus]);

  useEffect(() => { load(); }, [load]);

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const total = useMemo(() => bookings.length, [bookings]);

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-serif text-3xl text-[var(--ink)]">Foglalások</h1>
          <p className="text-sm text-[var(--ink-soft)] mt-1">{total} találat</p>
        </div>
      </div>

      <form onSubmit={onSearchSubmit} className="tk-card p-4 md:p-5 flex flex-col md:flex-row gap-3 items-stretch md:items-center mb-6">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Keresés: név, e-mail, telefon, helyszín, dátum"
            data-testid="bookings-search"
            className="tk-input pl-9"
          />
        </div>
        <div className="flex gap-3">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="tk-input md:w-44">
            <option value="">Minden státusz</option>
            {["pending","awaiting_payment","paid","confirmed","cancelled","expired"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} data-testid="filter-payment" className="tk-input md:w-40">
            <option value="">Minden fizetés</option>
            {["unpaid","paid","refunded"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="submit" className="tk-btn-primary !py-2.5"><Filter size={14} />Szűrés</button>
        </div>
      </form>

      <div className="tk-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--beige-100)] text-[var(--ink-soft)]">
              <tr className="text-left text-xs uppercase tracking-widest">
                <th className="px-4 py-3">Ügyfél</th>
                <th className="px-4 py-3">Esemény</th>
                <th className="px-4 py-3">Telefon</th>
                <th className="px-4 py-3">Csomag</th>
                <th className="px-4 py-3">Státusz</th>
                <th className="px-4 py-3">Fizetés</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-[var(--ink-soft)] italic">Betöltés…</td></tr>
              )}
              {!loading && bookings.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--ink-soft)] italic">Még nincs foglalás.</td></tr>
              )}
              {!loading && bookings.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[rgba(110,30,43,0.06)] hover:bg-[var(--beige-50)]">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--ink)]">{b.customer_name}</div>
                    <div className="text-xs text-[var(--ink-soft)]">{b.customer_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5"><CalIcon size={12} className="text-[var(--burgundy)]" />{b.event_date}</div>
                    <div className="text-xs text-[var(--ink-soft)]">{b.event_location}</div>
                  </td>
                  <td className="px-4 py-3 text-[var(--ink)]">
                    {b.device ? (
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded-full border border-[rgba(44,31,26,0.18)]" style={{ background: b.device.color || "#f5efe4" }} />
                        {b.device.name}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-[var(--ink-soft)]">{b.selected_package || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                  <td className="px-4 py-3"><PaymentBadge payment_status={b.payment_status} /></td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/bookings/${b.id}`} data-testid={`view-booking-${b.id}`} className="text-xs tk-link-soft underline">Megnyitás</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
